import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

const STORAGE_KEY = "acceptCookies";

export type CookieConsentStatus = "pending" | "accepted" | "rejected";

function readConsentState(): CookieConsentStatus {
  if (typeof window === "undefined") {
    return "pending";
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "true") {
    return "accepted";
  }
  if (stored === "false") {
    return "rejected";
  }

  // Remove legacy invalid values written by the old broken banner
  if (stored !== null) {
    localStorage.removeItem(STORAGE_KEY);
  }

  return "pending";
}

type CookieConsentContextValue = {
  status: CookieConsentStatus;
  accept: () => void;
  reject: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null
);

export function CookieConsentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [status, setStatus] = useState(readConsentState);

  const accept = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    setStatus("accepted");
  }, []);

  const reject = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "false");
    setStatus("rejected");
  }, []);

  return (
    <CookieConsentContext.Provider value={{ status, accept, reject }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (context == null) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
}
